import { WorkerRequirementMappingLevel } from "@clipboard-health/contract-worker-app-bff";
import { CardWithIntent, Illustration, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardContent, Stack } from "@mui/material";
import { USA_STATES } from "@src/appV2/lib/utils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath } from "react-router-dom";

import { Button } from "../../components/Button";
import { DOCUMENT_DETAILS_FULL_PATH } from "../../Documents/paths";
import {
  calculateMissingCredential,
  mapPlacementDetailRequirementsToPlacementDataRequirements,
} from "../../Documents/utils/calculateMissingCredential";
import { useIsSmallHorizontalSpacing } from "../../utils/useIsSmallHorizontalSpacing";
import { useIsSmallVerticalSpacing } from "../../utils/useIsSmallVerticalSpacing";
import { type PlacementDetail } from "../api/useGetPlacement";
import { useHiringQualificationRanking } from "../api/useHiringQualificationRanking";

export function PlacementCredentialRequirementsCard(props: { placementDetails: PlacementDetail }) {
  const { placementDetails } = props;

  const worker = useDefinedWorker();
  const { getHighestRankingQualification } = useHiringQualificationRanking();

  const isSmallVerticalSpacing = useIsSmallVerticalSpacing();
  const isSmallHorizontalSpacing = useIsSmallHorizontalSpacing();

  const { credentialRequirements = [], workplace } = placementDetails;

  const missingCredentialType = calculateMissingCredential(
    mapPlacementDetailRequirementsToPlacementDataRequirements(credentialRequirements)
  );

  if (credentialRequirements.length === 0 || !isDefined(missingCredentialType)) {
    return null;
  }

  const highestRankingSharedQualification = getHighestRankingQualification(
    (placementDetails.workerTypes ?? []).filter((qualification) =>
      worker.licensesData.map((licenseData) => licenseData.qualification).includes(qualification)
    )
  );

  const missingCredentialRequirement =
    missingCredentialType === "BASE_CREDENTIAL"
      ? credentialRequirements.find(
          (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.STATE
        )
      : credentialRequirements.find(
          (requirement) => requirement.attributes.level === WorkerRequirementMappingLevel.HCF
        );

  const stateAbbreviation =
    USA_STATES.find((state) => state.name === workplace.state)?.key ?? workplace.state;

  const documentDescriptionLabel =
    missingCredentialRequirement?.relationships?.requirement?.data?.attributes?.name ??
    "valid qualification";

  const description =
    missingCredentialType === "BASE_CREDENTIAL"
      ? `You need a ${documentDescriptionLabel} to work ${highestRankingSharedQualification} jobs${
          workplace.state ? ` in the state of ${workplace.state}` : ""
        }.`
      : `You need a ${documentDescriptionLabel} to work this job.`;

  const href = generatePath(DOCUMENT_DETAILS_FULL_PATH, {
    requirementId: missingCredentialRequirement?.relationships.requirement.data.id ?? "",
  });

  return (
    <CardWithIntent intent="neutral">
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          py: isSmallVerticalSpacing ? 1 : undefined,
          px: isSmallHorizontalSpacing ? 2 : 6,
        }}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={isSmallVerticalSpacing ? 2 : 5}
          sx={{
            textAlign: "center",
            textWrap: "balance",
            width: "100%",
            flexGrow: 1,
            mb: "auto",
          }}
        >
          <Illustration type="document-upload" />

          <Text semibold variant="h5">
            Upload{" "}
            {missingCredentialType === "BASE_CREDENTIAL" ? `${stateAbbreviation} State ` : ""}
            Qualification
          </Text>

          <Text
            color={(theme) => theme.palette.text.secondary}
            variant="body2"
            data-testid="bookability-sheet-description"
          >
            {description}
          </Text>
        </Stack>

        <Box sx={{ width: "100%", mt: isSmallVerticalSpacing ? 2 : 5 }}>
          <Button fullWidth href={href} variant="contained" size="medium">
            Upload
          </Button>
        </Box>
      </CardContent>
    </CardWithIntent>
  );
}
